import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { createSession } from "../lib/api";

/**
 * SessionScheduleModal — captain-only flow for scheduling a scrim or
 * league play session. The caller passes the already-loaded roster so
 * we don't refetch.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   team: { team_id, name, captain_user_id, game } — REQUIRED
 *   members: [{user_id, name}] — full roster (captain included)
 *   league: optional { league_id, name }
 *   challengeId: optional team_challenge_id to link the session back to
 *   onCreated: (session) => void
 *   onToast: (msg) => void
 */
export function SessionScheduleModal({
  open, onClose, team, members = [], league, challengeId, onCreated, onToast,
}) {
  const today = useMemo(() => new Date(), []);
  const defaultDate = new Date(today.getTime() + 24 * 3600 * 1000); // tomorrow
  const userTz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    []
  );

  const [title, setTitle] = useState("");
  const [date, setDate] = useState(toLocalDate(defaultDate));
  const [time, setTime] = useState("20:00");
  const [duration, setDuration] = useState(120); // minutes
  const [notes, setNotes] = useState("");
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !team?.team_id) return;
    setTitle(`${team.name || "Team"} Scrim`);
    // Pre-select every active roster member EXCEPT the captain (the
    // captain is the creator and auto-accepted).
    const initial = {};
    (members || []).forEach((m) => {
      if (m.user_id && m.user_id !== team.captain_user_id) initial[m.user_id] = true;
    });
    setPicked(initial);
  }, [open, team?.team_id, team?.captain_user_id, team?.name, members]);

  // Escape closes only this (topmost) modal. We attach as a `capture`
  // listener and stopPropagation so the underlying TeamHqModal's Esc
  // handler (if any) doesn't fire in the same keystroke.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      onToast?.("Title required");
      return;
    }
    const startsAt = new Date(`${date}T${time}:00`);
    if (isNaN(startsAt.getTime())) {
      onToast?.("Invalid start time");
      return;
    }
    const endsAt = new Date(startsAt.getTime() + duration * 60 * 1000);
    const invite_user_ids = Object.keys(picked).filter((k) => picked[k]);
    setBusy(true);
    try {
      const sess = await createSession({
        title: title.trim(),
        game: team?.game || "Other",
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        timezone: userTz,
        team_ids: team?.team_id ? [team.team_id] : [],
        league_id: league?.league_id,
        challenge_id: challengeId,
        invite_user_ids,
        notes: notes.trim(),
      });
      onToast?.(`📅 Session scheduled — ${invite_user_ids.length} invite(s) sent.`);
      onCreated?.(sess);
      onClose();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Could not create session");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="session-schedule-modal"
      onClick={(e) => {
        // Backdrop click closes only THIS modal. We also kill propagation
        // so the underlying Team HQ backdrop (rendered separately at z-[95])
        // doesn't dismiss itself in the same event.
        e.stopPropagation();
        onClose();
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-[#0a0a0a] border border-[#14b8a6]/30 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-calendar-plus mr-1" />SCHEDULE SESSION
            </p>
            <h3 className="font-display text-xl mt-1">{team?.name || "New session"}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="session-schedule-close"
            className="text-white/40 hover:text-white text-xl leading-none"
          >×</button>
        </div>

        <label className="block text-[10px] tracking-widest text-white/50 mb-1">TITLE</label>
        <input
          required
          data-testid="session-schedule-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Friday Scrim · BO5"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white mb-3 focus:border-[#14b8a6]/60 focus:outline-none"
        />

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-[10px] tracking-widest text-white/50 mb-1">DATE</label>
            <input
              required
              type="date"
              data-testid="session-schedule-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest text-white/50 mb-1">TIME ({userTz})</label>
            <input
              required
              type="time"
              data-testid="session-schedule-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            />
          </div>
        </div>

        <label className="block text-[10px] tracking-widest text-white/50 mb-1">DURATION</label>
        <select
          value={duration}
          onChange={(e) => setDuration(parseInt(e.target.value, 10))}
          data-testid="session-schedule-duration"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white mb-3"
        >
          <option value={60}>1 hour</option>
          <option value={90}>1.5 hours</option>
          <option value={120}>2 hours</option>
          <option value={180}>3 hours</option>
          <option value={240}>4 hours</option>
        </select>

        <label className="block text-[10px] tracking-widest text-white/50 mb-1">NOTES (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Map veto, comms, lineup …"
          data-testid="session-schedule-notes"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white mb-3 focus:border-[#14b8a6]/60 focus:outline-none"
        />

        {members.length > 0 && (
          <>
            <p className="text-[10px] tracking-widest text-white/50 mb-1">INVITE</p>
            <div className="space-y-1.5 mb-3 max-h-44 overflow-y-auto" data-testid="session-schedule-roster">
              {members
                .filter((m) => m.user_id !== team?.captain_user_id)
                .map((m) => (
                  <label
                    key={m.user_id}
                    data-testid={`session-schedule-roster-row-${m.user_id}`}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-black/40 border border-white/5 hover:border-[#14b8a6]/40 cursor-pointer"
                  >
                    <span className="text-sm text-white truncate">{m.name}</span>
                    <input
                      type="checkbox"
                      checked={!!picked[m.user_id]}
                      onChange={(e) => setPicked((s) => ({ ...s, [m.user_id]: e.target.checked }))}
                      className="accent-[#14b8a6] h-4 w-4"
                    />
                  </label>
                ))}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="session-schedule-submit"
          className="w-full text-[11px] tracking-widest px-4 py-3 rounded-xl bg-[#14b8a6] text-black hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "SCHEDULING…" : "SCHEDULE & SEND INVITES"}
        </button>
      </form>
    </div>,
    document.body,
  );
}

function toLocalDate(d) {
  // YYYY-MM-DD in the user's tz for the <input type=date> value.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default SessionScheduleModal;
